import { useContext, useEffect, useRef } from 'react';
import { useAuth } from '@clerk/react';
import { useLocation } from 'react-router-dom';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { DiffitUserContext } from '../../contexts/DiffitUserContext';
import { useSignInModal } from '../../contexts/SignInModalContext';
import { Mixpanel } from '../../services/Mixpanel';
import { resolveReferralCode } from '../../services/ReferralService';
import { markReferralWelcomed, readUnwelcomedReferralCode } from '../../utils/referralAttribution';
import {
  armOpensModal,
  referralWelcomeArm,
  REFERRAL_WELCOME_ARM,
} from '../../utils/referralWelcome';

/**
 * Greets a visitor who lands on the homepage holding a referral capture: resolves whose
 * link it was and, in the treatment arms, opens the sign-up modal on the spot.
 *
 * Lives in Base beside ForcedSignupPrompt and, like it, renders nothing — Base hosts the
 * sign-in modal provider and so cannot consume it from the component that renders it.
 */
export const ReferralWelcomePrompt = () => {
  const location = useLocation();
  const { isSignedIn, isLoaded } = useAuth();
  const { isLDidentified } = useContext(DiffitUserContext);
  const { experimentReferralSignupName } = useFlags();
  const showSignInModal = useSignInModal();
  const decidedRef = useRef(false);

  useEffect(() => {
    if (location.pathname !== '/' || !isLoaded || isSignedIn || !isLDidentified) return;
    if (decidedRef.current) return;

    const code = readUnwelcomedReferralCode();
    if (!code) return;

    decidedRef.current = true;
    let cancelled = false;

    resolveReferralCode(code)
      .then((response) => {
        if (cancelled) {
          decidedRef.current = false;
          return;
        }
        const referrerGivenName = response?.data?.referrer_given_name || '';
        if (!referrerGivenName) return;

        markReferralWelcomed();

        const arm = referralWelcomeArm(experimentReferralSignupName);

        Mixpanel.track('Referral welcome eligible', { arm });
        if (!armOpensModal(arm)) return;

        showSignInModal({
          referrerGivenName: arm === REFERRAL_WELCOME_ARM.NAMED ? referrerGivenName : '',
        });
      })
      .catch(() => {
        if (!cancelled) decidedRef.current = false;
      });

    return () => {
      cancelled = true;
    };
  }, [
    location.pathname,
    isLoaded,
    isSignedIn,
    isLDidentified,
    experimentReferralSignupName,
    showSignInModal,
  ]);

  return null;
};
